// CourseProgressFunctions/calculateProgressForMinor.js

import findRequirementCourses from './FindRequirementCourses';
import { findElectiveCourses } from './FindElectiveCourses';

const updatedCalculateProgressForMinor = (
  minor,
  fetchedMinors,
  fetchedCourseData,
  finalizedSchedule,
  completedSemesters
) => {
  if (!minor || !fetchedMinors) {
    return {
      takenCoursesCount: 0, // Total taken courses count
      planCoursesCount: 0,  // Remaining planned courses count
      totCoursesCount: 0,   // Total courses count for the minor
    };
  }

  // Aggregate courses from all years and all dynamic semesters
  let courseArray = [];
  for (let i = 1; i <= 5; i++) { // handle up to five years
    const year = `year${i}`;
    if (finalizedSchedule[year]) {
      Object.keys(finalizedSchedule[year]).forEach((semesterKey) => {
        const courses =
          Array.isArray(finalizedSchedule[year][semesterKey].courses)
            ? finalizedSchedule[year][semesterKey].courses
            : [];
        courseArray.push(...courses);
      });
    }
  }

  // Get the student courses that match the aggregated course IDs
  const studentCourses = fetchedCourseData
    ? fetchedCourseData.filter(course =>
        courseArray.includes(course.courseInfo_courseNumber)
      )
    : [];

  // Get the minor courses and determine the total courses (excluding Milestone courses)
  const minorCourses = Object.values(fetchedMinors[minor]);
  const totalCourses = minorCourses.filter(
    (minorCourse) => minorCourse.courseType !== 'Milestone'
  ).length;

  // Determine taken courses by iterating over all dynamic term keys in completedSemesters
  const takenCoursesSet = new Set();
  Object.keys(completedSemesters).forEach((year) => {
    if (completedSemesters[year]) {
      Object.keys(completedSemesters[year]).forEach((semesterKey) => {
        if (completedSemesters[year][semesterKey]) {
          const completedCourses =
            finalizedSchedule[year]?.[semesterKey]?.courses || [];
          completedCourses.forEach((course) => {
            takenCoursesSet.add(course);
          });
        }
      });
    }
  });
  const takenCoursesArray = Array.from(takenCoursesSet);
  const takenCourses = fetchedCourseData.filter(course =>
    takenCoursesArray.includes(course.courseInfo_courseNumber)
  );

  // Identify taken minor courses (those labeled as 'Minor Course' or with no Requirements)
  const minorRequiredSpecificCourses = minorCourses.filter(
    (minorCourse) =>
      minorCourse.courseType === 'Minor Course' || minorCourse.Requirements === ''
  );
  const takenMinorCoursesArray = takenCourses.filter(course =>
    minorRequiredSpecificCourses.some(
      (minorCourse) =>
        minorCourse.courseInfo_courseNumber === course.courseInfo_courseNumber
    )
  );

  // Identify taken elective courses using the helper function
  const takenElectiveCoursesArray = findElectiveCourses(
    minorCourses.filter(course => course.courseType === 'Elective'),
    takenCourses
  );

  // Identify taken requirement courses using the helper function
  const takenRequirementCoursesArray = findRequirementCourses(
    minorCourses.filter(
      (course) => course.Requirements || course.courseType === 'Requirement'
    ),
    takenCourses,
    takenElectiveCoursesArray
  );

  // Calculate all applicable minor courses from the student's schedule
  const allMinorCoursesArray = studentCourses.filter(course =>
    minorRequiredSpecificCourses.some(
      (minorCourse) =>
        minorCourse.courseInfo_courseNumber === course.courseInfo_courseNumber
    )
  );

  // Calculate all elective courses and requirement courses from the student's schedule
  const allElectiveCoursesArray = findElectiveCourses(
    minorCourses.filter(course => course.courseType === 'Elective'),
    studentCourses
  );

  const allRequirementCoursesArray = findRequirementCourses(
    minorCourses.filter(
      (course) => course.Requirements || course.courseType === 'Requirement'
    ),
    studentCourses,
    allElectiveCoursesArray
  );

  // Calculate counts for taken and planned courses
  const takenCoursesLengths =
    takenMinorCoursesArray.length +
    takenElectiveCoursesArray.length +
    takenRequirementCoursesArray.length;

  const allCoursesLengths =
    allMinorCoursesArray.length +
    allElectiveCoursesArray.length +
    allRequirementCoursesArray.length -
    takenCoursesLengths;

  return {
    takenCoursesCount: takenCoursesLengths, // Total taken courses count
    planCoursesCount: allCoursesLengths,      // Remaining planned courses count
    totCoursesCount: totalCourses,            // Total courses count for the minor
  };
};

export default updatedCalculateProgressForMinor;
